import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TbArrowRight } from 'react-icons/tb';
import _ from 'lodash';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { BalanceStats, BankTransactionWithParticipation } from '../../../types/bankAccount';
import connectionService from '../../../services/connectionService';
import * as bankAccountService from '../../../services/bankAccountService';
import { Connection } from '../../../redux/slices/connectionsSlice';

// Props can be added later for real data

interface MemberData {
  name: string;
  personalBalance: number;
  contributionBalance: number;
  totalBalance: number;
}

function MemberBalanceCards(): JSX.Element {
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.overview.memberBalanceCards',
  });
  const navigate = useNavigate();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [transactions, setTransactions] = useState<BankTransactionWithParticipation[] | null>();
  const [contributionBalances, setContributionBalances] = useState<Record<
    number,
    BalanceStats
  > | null>();

  useEffect(() => {
    Promise.all([
      connectionService.getConnections(),
      bankAccountService.getAllTransactions(),
      bankAccountService.getContributionBalancesForConnections(),
    ])
      .then(([conns, tns, contrib]) => {
        setConnections(conns.filter((c: Connection) => c.type === 'B2C_CONNECTION'));
        setTransactions(tns);
        setContributionBalances(contrib);
      })
      .catch(() => {
        setConnections([]);
        setTransactions(null);
        setContributionBalances(null);
      });
  }, []);

  const members = useMemo((): MemberData[] => {
    if (!transactions || !contributionBalances) return [];

    const aggregated = getPersonalBalances(transactions);
    const declaredAmounts = getDeclarationAmounts(transactions);

    const byConnectionId: Record<string, MemberData> = {};

    // Start with any connection we know
    connections.forEach((c) => {
      const name = `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`.trim();
      byConnectionId[c.id] = {
        name,
        personalBalance: 0,
        contributionBalance: 0,
        totalBalance: 0,
      };
    });

    // Apply personal balances
    aggregated.forEach(([connId, { balance }]) => {
      const declared = declaredAmounts?.[+connId] ?? 0;
      const personalBalance = balance - declared;
      if (!byConnectionId[connId]) {
        byConnectionId[connId] = {
          name: `#${connId}`,
          personalBalance: 0,
          contributionBalance: 0,
          totalBalance: 0,
        };
      }
      byConnectionId[connId].personalBalance = personalBalance;
    });

    // Apply contribution balances
    Object.entries(contributionBalances).forEach(([connId, stats]) => {
      if (!byConnectionId[connId]) {
        byConnectionId[connId] = {
          name: `#${connId}`,
          personalBalance: 0,
          contributionBalance: 0,
          totalBalance: 0,
        };
      }
      byConnectionId[connId].contributionBalance = stats.balance;
    });

    // Compute totals
    Object.values(byConnectionId).forEach((m) => {
      m.totalBalance = (m.personalBalance || 0) + (m.contributionBalance || 0);
    });

    // Sort by total balance desc
    return Object.values(byConnectionId).sort((a, b) => b.totalBalance - a.totalBalance);
  }, [transactions, contributionBalances, connections]);

  const topFiveMembers = members.slice(0, 5);
  const bottomFiveMembers = members.slice(-5).reverse();

  const handleSeeMore = () => {
    navigate('/financial/reports', { state: { activeTab: 'members' } });
  };

  const renderMemberRow = (member: MemberData, index: number) => (
    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.name}</td>
      <td className="px-4 py-3 text-right text-sm text-gray-900">
        {formatEuroCentsWithSign(member.personalBalance, i18n.language)}
      </td>
      <td className="px-4 py-3 text-right text-sm text-gray-900">
        {formatEuroCentsWithSign(member.contributionBalance, i18n.language)}
      </td>
      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
        {formatEuroCentsWithSign(member.totalBalance, i18n.language)}
      </td>
    </tr>
  );

  return (
    <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* Top 5 Members */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{t('topFive.title')}</h2>
          <button
            type="button"
            onClick={handleSeeMore}
            className="flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/5">
            <span>{t('seeAll')}</span>
            <TbArrowRight size={16} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  {t('table.memberName')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  {t('table.personalBalance')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  {t('table.contributionBalance')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  {t('table.totalBalance')}
                </th>
              </tr>
            </thead>
            <tbody>{topFiveMembers.map((member, index) => renderMemberRow(member, index))}</tbody>
          </table>
        </div>
      </div>

      {/* Bottom 5 Members */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{t('bottomFive.title')}</h2>
          <button
            type="button"
            onClick={handleSeeMore}
            className="flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/5">
            <span>{t('seeAll')}</span>
            <TbArrowRight size={16} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                  {t('table.memberName')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  {t('table.personalBalance')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  {t('table.contributionBalance')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  {t('table.totalBalance')}
                </th>
              </tr>
            </thead>
            <tbody>
              {bottomFiveMembers.map((member, index) => renderMemberRow(member, index))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default MemberBalanceCards;

function getPersonalBalances(tns: BankTransactionWithParticipation[]) {
  return _.chain(tns)
    .flatMap((t) => t.participations)
    .filter((t) => !!t)
    .groupBy((t) => +t!.connectionId)
    .mapValues((ps) => ({
      income: ps.filter((p) => p!.amount > 0).reduce((sum, p) => sum + p!.amount, 0),
      expenses: ps.filter((p) => p!.amount < 0).reduce((sum, p) => sum + p!.amount, 0),
      balance: ps.reduce((sum, p) => sum + p!.amount, 0),
    }))
    .entries()
    .value();
}

function getDeclarationAmounts(tns: BankTransactionWithParticipation[]): Record<number, number> {
  return _.chain(tns)
    .filter((tn) => !!tn.payerId)
    .groupBy((tn) => tn.payerId)
    .mapValues((xs) => xs.reduce((sum, x) => sum + x.amount, 0))
    .value();
}
